import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, API } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, PencilSimple, Trash, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const IMG_BASE = API.replace(/\/api$/, "");
const STATUSES = ["pending", "scheduled", "picked_up", "qc_in_progress", "offer_shared", "price_accepted", "completed", "cancelled"];
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

async function downloadXlsx(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Export failed"); }
}

// Excel Export + Import toolbar (shared by Catalog & Price Master)
function ExcelBar({ exportPath, exportName, importPath, onDone, label }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const doImport = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(importPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [];
      for (const [k, v] of Object.entries(data)) {
        if (v && typeof v === "object" && "created" in v) parts.push(`${k}: +${v.created}/~${v.updated}`);
        else if (k === "created") parts.push(`+${v} new`);
        else if (k === "updated") parts.push(`~${v} updated`);
      }
      toast.success(`${label} imported (${parts.join(", ")})`);
      if ((data.errors || []).length) toast.warning(`${data.errors.length} row(s) skipped — ${data.errors.slice(0, 3).join("; ")}`);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Import failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => downloadXlsx(exportPath, exportName)} data-testid={`export-${label.toLowerCase().replace(/ /g, "-")}`}>
        <DownloadSimple size={15} /> Export Excel
      </Button>
      <Button variant="outline" className="rounded-sm gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`import-${label.toLowerCase().replace(/ /g, "-")}`}>
        <UploadSimple size={15} /> {busy ? "Importing…" : "Import Excel"}
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { doImport(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

// Small active/inactive pill toggle
function ActiveToggle({ active, onToggle, testid }) {
  return (
    <button onClick={onToggle} data-testid={testid}
      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100" : "bg-secondary text-muted-foreground border-border hover:bg-secondary/70"}`}>
      {active ? "Active" : "Disabled"}
    </button>
  );
}

export default function SellDevicesAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/sell/admin/categories").then((r) => setCats(r.data)); }, []);

  return (
    <>
      <PageHeader title="Sell Devices" subtitle="Buyback orders, pricing & masters" />
      <div className="p-6">
        <Tabs defaultValue="orders">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="executives" data-testid="tab-executives">Pickup Executives</TabsTrigger>
            {isAdmin && <TabsTrigger value="prices" data-testid="tab-prices">Price Master</TabsTrigger>}
            {isAdmin && <TabsTrigger value="questions" data-testid="tab-questions">Questions</TabsTrigger>}
            {isAdmin && <TabsTrigger value="labels" data-testid="tab-labels">Image Labels</TabsTrigger>}
            {isAdmin && <TabsTrigger value="slots" data-testid="tab-slots">Pickup Slots</TabsTrigger>}
            {isAdmin && <TabsTrigger value="catalog" data-testid="tab-catalog">Catalog</TabsTrigger>}
          </TabsList>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="executives"><ExecutiveTab /></TabsContent>
          {isAdmin && <TabsContent value="prices"><PriceTab cats={cats} /></TabsContent>}
          {isAdmin && <TabsContent value="questions"><QuestionTab cats={cats} /></TabsContent>}
          {isAdmin && <TabsContent value="labels"><LabelTab /></TabsContent>}
          {isAdmin && <TabsContent value="slots"><SlotTab /></TabsContent>}
          {isAdmin && <TabsContent value="catalog"><CatalogTab cats={cats} reloadCats={() => api.get("/sell/admin/categories").then((r) => setCats(r.data))} /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

function OrdersTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/sell/admin/orders", { params: { status } }).then((r) => setRows(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  const openDetail = (o) => api.get(`/sell/admin/orders/${o.id}`).then((r) => setDetail(r.data));
  return (
    <div className="mt-4 space-y-3">
      <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
        <SelectTrigger className="rounded-sm w-56" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead>Booking</TableHead><TableHead>Customer</TableHead><TableHead>Device</TableHead>
            <TableHead>Pincode</TableHead><TableHead className="text-right">Est.</TableHead><TableHead>Executive</TableHead><TableHead>Pickup</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="sell-orders-table">
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No orders</TableCell></TableRow>}
            {rows.map((o) => (
              <TableRow key={o.id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(o)} data-testid={`order-row-${o.booking_id}`}>
                <TableCell className="font-mono font-semibold">{o.booking_id}</TableCell>
                <TableCell>{o.customer?.name}<div className="text-xs text-muted-foreground">{o.customer?.phone}</div></TableCell>
                <TableCell className="text-sm">{o.category_name} {o.brand_name} {o.model_name}</TableCell>
                <TableCell className="font-mono text-xs">{o.customer?.pincode}</TableCell>
                <TableCell className="text-right font-mono">{fmt(o.estimated_price)}</TableCell>
                <TableCell className="text-xs">{o.assigned_executive_name || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs">{o.pickup_date}<div className="text-muted-foreground">{o.pickup_slot}</div></TableCell>
                <TableCell><span className="text-xs font-semibold bg-secondary px-2 py-1 rounded">{o.status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="order-detail">
          {detail && <OrderDetail order={detail} onChange={(o) => { setDetail(o); load(); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function OrderDetail({ order, onChange }) {
  const detail = order;
  const [execs, setExecs] = useState([]);
  const [execId, setExecId] = useState(detail.assigned_executive_id || "");
  const [sellPartners, setSellPartners] = useState([]);
  const [partnerId, setPartnerId] = useState(detail.partner_id || "");
  const [questions, setQuestions] = useState([]);
  const [insAns, setInsAns] = useState({});
  const [override, setOverride] = useState("");
  const [ovReason, setOvReason] = useState("");
  const [coAmount, setCoAmount] = useState("");
  const [coReason, setCoReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/sell/admin/executives").then((r) => setExecs(r.data.filter((e) => e.active))); }, []);
  useEffect(() => { api.get("/sell/admin/sell-partners", { params: { pincode: detail.customer?.pincode || "" } }).then((r) => setSellPartners(r.data)).catch(() => {}); }, [detail.customer?.pincode]);
  useEffect(() => {
    api.get(`/sell/questions?category_id=${detail.category_id}&brand_id=${detail.brand_id || ""}`).then((r) => {
      setQuestions(r.data);
      const prefill = {};
      (detail.inspection?.answers || []).forEach((a) => { prefill[a.question_id] = a.values || a.value; });
      setInsAns(prefill);
    });
  }, [detail.category_id, detail.brand_id, detail.id]);

  const changeStatus = async (st) => { const { data } = await api.put(`/sell/admin/orders/${detail.id}/status`, { status: st }); toast.success("Status updated"); onChange(data); };
  const assignExec = async () => {
    if (!execId) return; setBusy(true);
    try { const { data } = await api.put(`/sell/admin/orders/${detail.id}/assign-executive`, { executive_id: execId }); toast.success("Executive assigned"); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false);
  };
  const assignPartner = async () => {
    if (!partnerId) return; setBusy(true);
    try { const { data } = await api.put(`/sell/admin/orders/${detail.id}/assign-partner`, { partner_id: partnerId }); toast.success("Pickup partner assigned"); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false);
  };
  const insPayload = () => Object.entries(insAns).map(([qid, v]) => Array.isArray(v) ? { question_id: qid, values: v } : { question_id: qid, value: v });
  const submitInspection = async () => {
    setBusy(true);
    try {
      const body = { answers: insPayload() };
      if (override !== "") { body.override_price = Number(override); body.override_reason = ovReason; }
      const { data } = await api.put(`/sell/admin/orders/${detail.id}/inspection`, body);
      toast.success(`QC saved. Final price ₹${data.final_price}`); onChange(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false);
  };
  const shareCounterOffer = async () => {
    if (!coAmount) { toast.error("Enter offer amount"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/sell/admin/orders/${detail.id}/counter-offer`, { amount: Number(coAmount), reason: coReason }); toast.success("Counter offer shared & emailed"); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false);
  };
  const manualResponse = async (decision) => {
    setBusy(true);
    try { const { data } = await api.post(`/sell/admin/orders/${detail.id}/offer-response`, { decision, note: "Recorded manually" }); toast.success(`Marked ${decision}ed`); onChange(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false);
  };

  const co = detail.counter_offer;
  return (
    <>
      <SheetHeader><SheetTitle className="font-heading">{detail.booking_id}</SheetTitle></SheetHeader>
      <div className="py-5 space-y-5 text-sm">
        <div className="bg-secondary rounded-sm p-3">
          <p className="font-semibold">{detail.customer?.name} · {detail.customer?.phone}</p>
          <p className="text-muted-foreground">{detail.customer?.email}</p>
          <p className="text-muted-foreground mt-1">{detail.customer?.address}, {detail.customer?.city} {detail.customer?.pincode}</p>
        </div>
        <div>
          <p className="label-caps mb-1">Device</p>
          <p>{detail.category_name} · {detail.brand_name} · {detail.model_name || (detail.manual_model && Object.values(detail.manual_model).join(", "))}</p>
          <p className="mt-1">Estimate: <b>{fmt(detail.estimated_price)}</b> · {detail.tier_name} · {detail.score_pct}%
            {detail.final_price != null && <> · Final: <b className="text-primary">{fmt(detail.final_price)}</b></>}</p>
        </div>

        <div>
          <Label className="label-caps">Update status</Label>
          <Select value={detail.status} onValueChange={changeStatus}>
            <SelectTrigger className="rounded-sm mt-1.5" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Assign pickup partner (reuses service partner pool) */}
        <div className="border border-border rounded-sm p-3" data-testid="assign-partner-section">
          <Label className="label-caps">Pickup Partner</Label>
          <div className="flex gap-2 mt-1.5">
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger className="rounded-sm" data-testid="assign-partner-select"><SelectValue placeholder="Select Sell-enabled partner" /></SelectTrigger>
              <SelectContent>
                {sellPartners.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No Sell-enabled partner covers this pincode</div>}
                {sellPartners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.city ? ` · ${p.city}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button className="rounded-sm" disabled={busy || !partnerId} onClick={assignPartner} data-testid="assign-partner-btn">Assign</Button>
          </div>
          {detail.partner_name && <p className="text-xs text-muted-foreground mt-1.5">Assigned: <b>{detail.partner_name}</b> · {detail.partner_acceptance || "pending"}</p>}
        </div>

        {/* Assign pickup executive */}
        <div className="border border-border rounded-sm p-3">
          <Label className="label-caps">Pickup Executive</Label>
          <div className="flex gap-2 mt-1.5">
            <Select value={execId} onValueChange={setExecId}>
              <SelectTrigger className="rounded-sm" data-testid="assign-exec-select"><SelectValue placeholder="Select executive" /></SelectTrigger>
              <SelectContent>{execs.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.phone}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="rounded-sm" disabled={busy || !execId} onClick={assignExec} data-testid="assign-exec-btn">Assign</Button>
          </div>
          {detail.assigned_executive_name && <p className="text-xs text-muted-foreground mt-1.5">Assigned: <b>{detail.assigned_executive_name}</b></p>}
        </div>

        {/* QC / Inspection */}
        <div className="border border-border rounded-sm p-3" data-testid="qc-section">
          <p className="font-heading font-semibold mb-2">QC / Inspection</p>
          <div className="space-y-3">
            {questions.map((q) => (
              <div key={q.id} data-testid={`qc-q-${q.id}`}>
                <Label className="text-xs font-semibold">{q.text}</Label>
                {(q.qtype === "radio" || q.qtype === "yesno" || q.qtype === "dropdown") && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {q.options.map((o) => (
                      <button key={o.label} onClick={() => setInsAns({ ...insAns, [q.id]: o.label })}
                        className={`px-3 py-1 rounded-full border text-xs ${insAns[q.id] === o.label ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{o.label}</button>
                    ))}
                  </div>
                )}
                {q.qtype === "multiselect" && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {q.options.map((o) => {
                      const arr = Array.isArray(insAns[q.id]) ? insAns[q.id] : []; const on = arr.includes(o.label);
                      return <button key={o.label} onClick={() => setInsAns({ ...insAns, [q.id]: on ? arr.filter((x) => x !== o.label) : [...arr, o.label] })}
                        className={`px-3 py-1 rounded-full border text-xs ${on ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{o.label}</button>;
                    })}
                  </div>
                )}
                {q.qtype === "text" && <Input className="rounded-sm mt-1" value={insAns[q.id] || ""} onChange={(e) => setInsAns({ ...insAns, [q.id]: e.target.value })} />}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div><Label className="label-caps">Override price (₹)</Label><Input type="number" placeholder="auto" className="rounded-sm mt-1" value={override} onChange={(e) => setOverride(e.target.value)} data-testid="qc-override-price" /></div>
              <div><Label className="label-caps">Override reason</Label><Input placeholder="required if overriding" className="rounded-sm mt-1" value={ovReason} onChange={(e) => setOvReason(e.target.value)} data-testid="qc-override-reason" /></div>
            </div>
            <Button className="rounded-sm w-full" disabled={busy} onClick={submitInspection} data-testid="qc-submit">Save QC & compute final price</Button>
            {detail.inspection && (
              <p className="text-xs text-muted-foreground">Last QC: computed {fmt(detail.inspection.computed_price)} ({detail.inspection.tier_name}) → final <b>{fmt(detail.inspection.final_price)}</b>{detail.inspection.override && <span className="text-amber-600"> · override: {detail.inspection.override.reason}</span>}</p>
            )}
          </div>
        </div>

        {/* Counter offer */}
        <div className="border border-border rounded-sm p-3" data-testid="counter-offer-section">
          <p className="font-heading font-semibold mb-2">Counter Offer</p>
          {co && (
            <div className="mb-3 text-xs bg-secondary rounded-sm p-2">
              <p>Offer <b>{fmt(co.amount)}</b> · status <b>{co.status}</b>{co.reason && <> · {co.reason}</>}</p>
              {co.responded_via && <p className="text-muted-foreground">Responded via {co.responded_via}</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="label-caps">Amount (₹)</Label><Input type="number" className="rounded-sm mt-1" value={coAmount} onChange={(e) => setCoAmount(e.target.value)} data-testid="co-amount" /></div>
            <div><Label className="label-caps">Reason</Label><Input className="rounded-sm mt-1" value={coReason} onChange={(e) => setCoReason(e.target.value)} data-testid="co-reason" /></div>
          </div>
          <Button className="rounded-sm w-full mt-2" disabled={busy} onClick={shareCounterOffer} data-testid="co-share">Share offer with customer (email)</Button>
          {co && co.status === "pending" && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Button variant="outline" className="rounded-sm border-red-300 text-red-600" disabled={busy} onClick={() => manualResponse("reject")} data-testid="co-manual-reject">Mark Rejected</Button>
              <Button className="rounded-sm bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => manualResponse("accept")} data-testid="co-manual-accept">Mark Accepted</Button>
            </div>
          )}
        </div>

        <div>
          <p className="label-caps mb-1">Customer answers</p>
          <div className="space-y-1">
            {(detail.customer_answers || []).map((a, i) => <div key={i} className="text-xs">• {a.value || (a.values || []).join(", ")}</div>)}
          </div>
        </div>
        {detail.media?.length > 0 && (
          <div>
            <p className="label-caps mb-1">Media</p>
            <div className="grid grid-cols-4 gap-2">
              {detail.media.map((m) => (
                <a key={m.id} href={`${IMG_BASE}${m.url}`} target="_blank" rel="noreferrer" className="block">
                  {m.content_type?.startsWith("video") ? <div className="aspect-square bg-secondary rounded flex items-center justify-center text-xs">▶ {m.label}</div>
                    : <img src={`${IMG_BASE}${m.url}`} alt={m.label} className="aspect-square object-cover rounded" />}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ExecutiveTab() {
  const { rows, load } = useCrud("/sell/admin/executives");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", pincodes: [], active: true });
  const [pinStr, setPinStr] = useState("");
  const [editId, setEditId] = useState(null);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone required"); return; }
    const payload = { ...form, pincodes: pinStr.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean) };
    try { if (editId) await api.put(`/sell/admin/executives/${editId}`, payload); else await api.post("/sell/admin/executives", payload); toast.success("Saved"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (e) => { if (window.confirm("Delete executive?")) { await api.delete(`/sell/admin/executives/${e.id}`); load(); } };
  return (
    <div className="mt-4 space-y-3">
      <Button className="rounded-sm gap-2" onClick={() => { setForm({ name: "", phone: "", email: "", pincodes: [], active: true }); setPinStr(""); setEditId(null); setOpen(true); }} data-testid="add-exec-btn"><Plus size={15} /> Add Executive</Button>
      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <Table><TableHeader><TableRow className="bg-secondary/50"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Pincodes</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
          <TableBody data-testid="exec-table">
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No executives yet</TableCell></TableRow>}
            {rows.map((e) => (
              <TableRow key={e.id} data-testid={`exec-row-${e.id}`}>
                <TableCell className="font-medium">{e.name}</TableCell><TableCell className="font-mono text-xs">{e.phone}</TableCell><TableCell className="text-xs">{e.email}</TableCell>
                <TableCell className="text-xs">{(e.pincodes || []).join(", ")}</TableCell><TableCell>{e.active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm(e); setPinStr((e.pincodes || []).join(", ")); setEditId(e.id); setOpen(true); }}><PencilSimple size={15} /></Button><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(e)}><Trash size={15} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
      </div>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent data-testid="exec-form"><SheetHeader><SheetTitle>{editId ? "Edit" : "Add"} Pickup Executive</SheetTitle></SheetHeader>
        <div className="py-5 space-y-3">
          <div><Label className="label-caps">Name</Label><Input className="rounded-sm mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exec-name" /></div>
          <div><Label className="label-caps">Phone</Label><Input className="rounded-sm mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="exec-phone" /></div>
          <div><Label className="label-caps">Email</Label><Input className="rounded-sm mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="exec-email" /></div>
          <div><Label className="label-caps">Coverage pincodes (comma/space separated)</Label><Input className="rounded-sm mt-1.5" value={pinStr} onChange={(e) => setPinStr(e.target.value)} data-testid="exec-pincodes" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-primary" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        </div>
        <SheetFooter><Button className="rounded-sm" onClick={save} data-testid="exec-save">Save</Button></SheetFooter></SheetContent></Sheet>
    </div>
  );
}

function useCrud(path) {
  const [rows, setRows] = useState([]);
  const load = useCallback((params) => api.get(path, { params }).then((r) => setRows(r.data)), [path]);
  return { rows, setRows, load };
}

function PriceTab({ cats }) {
  const [cat, setCat] = useState("");
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const { rows, load } = useCrud("/sell/admin/prices");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand_id: "", model_id: "", base_price: "", scrap_price: "", flawless: 75, reliable: 60, worn: 40 });
  const [editId, setEditId] = useState(null);

  useEffect(() => { if (cat) { load({ category_id: cat }); api.get(`/sell/admin/brands?category_id=${cat}`).then((r) => setBrands(r.data)); } }, [cat, load]);
  useEffect(() => { if (form.brand_id) api.get(`/sell/admin/models?brand_id=${form.brand_id}`).then((r) => setModels(r.data)); }, [form.brand_id]);

  const save = async () => {
    const payload = { category_id: cat, brand_id: form.brand_id, model_id: form.model_id, base_price: Number(form.base_price), scrap_price: Number(form.scrap_price || 0), tier_percentages: { flawless: Number(form.flawless), reliable: Number(form.reliable), worn: Number(form.worn) } };
    try {
      if (editId) await api.put(`/sell/admin/prices/${editId}`, payload); else await api.post("/sell/admin/prices", payload);
      toast.success("Saved"); setOpen(false); load({ category_id: cat });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";
  const modelName = (id) => models.find((m) => m.id === id)?.name || rows.find((r) => r.model_id === id)?.model_id || "—";

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="rounded-sm w-64" data-testid="price-cat-select"><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <ExcelBar label="Price Master" exportPath="/sell/admin/prices/export" exportName="sell_price_master.xlsx" importPath="/sell/admin/prices/import" onDone={() => cat && load({ category_id: cat })} />
        {cat && <Button className="rounded-sm gap-2 ml-auto" onClick={() => { setForm({ brand_id: "", model_id: "", base_price: "", scrap_price: "", flawless: 75, reliable: 60, worn: 40 }); setEditId(null); setOpen(true); }} data-testid="add-price-btn"><Plus size={15} /> Add Price</Button>}
      </div>
      {cat && (
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50"><TableHead>Brand</TableHead><TableHead>Model</TableHead><TableHead className="text-right">Base</TableHead><TableHead className="text-right">Scrap</TableHead><TableHead>Tiers %</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
            <TableBody data-testid="price-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No price rows</TableCell></TableRow>}
              {rows.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{brandName(p.brand_id)}</TableCell>
                  <TableCell className="text-sm">{modelName(p.model_id)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(p.base_price)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(p.scrap_price)}</TableCell>
                  <TableCell className="text-xs">F{p.tier_percentages?.flawless} R{p.tier_percentages?.reliable} W{p.tier_percentages?.worn}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm({ brand_id: p.brand_id, model_id: p.model_id, base_price: p.base_price, scrap_price: p.scrap_price, flawless: p.tier_percentages?.flawless, reliable: p.tier_percentages?.reliable, worn: p.tier_percentages?.worn }); setEditId(p.id); setOpen(true); }}><PencilSimple size={15} /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="overflow-y-auto" data-testid="price-form">
          <SheetHeader><SheetTitle>{editId ? "Edit" : "Add"} Price</SheetTitle></SheetHeader>
          <div className="py-5 space-y-3">
            <div><Label className="label-caps">Brand</Label><Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v, model_id: "" })}><SelectTrigger className="rounded-sm mt-1.5"><SelectValue placeholder="Brand" /></SelectTrigger><SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="label-caps">Model</Label><Select value={form.model_id} onValueChange={(v) => setForm({ ...form, model_id: v })}><SelectTrigger className="rounded-sm mt-1.5"><SelectValue placeholder="Model" /></SelectTrigger><SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Base Price</Label><Input type="number" className="rounded-sm mt-1.5" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="price-base" /></div>
              <div><Label className="label-caps">Scrap Price</Label><Input type="number" className="rounded-sm mt-1.5" value={form.scrap_price} onChange={(e) => setForm({ ...form, scrap_price: e.target.value })} data-testid="price-scrap" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["flawless", "reliable", "worn"].map((t) => <div key={t}><Label className="label-caps">{t} %</Label><Input type="number" className="rounded-sm mt-1.5" value={form[t]} onChange={(e) => setForm({ ...form, [t]: e.target.value })} /></div>)}
            </div>
          </div>
          <SheetFooter><Button className="rounded-sm" onClick={save} data-testid="price-save">Save</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function QuestionTab({ cats }) {
  const [cat, setCat] = useState("");
  const { rows, load } = useCrud("/sell/admin/questions");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ text: "", qtype: "radio", mandatory: true, order: 0, options: [] });
  const [editId, setEditId] = useState(null);
  useEffect(() => { if (cat) load({ category_id: cat }); }, [cat, load]);
  const save = async () => {
    const payload = { ...form, category_id: cat, brand_id: "", options: form.options.map((o) => ({ label: o.label, points: Number(o.points) || 0, critical: !!o.critical })) };
    try { if (editId) await api.put(`/sell/admin/questions/${editId}`, payload); else await api.post("/sell/admin/questions", payload); toast.success("Saved"); setOpen(false); load({ category_id: cat }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (q) => { if (window.confirm("Delete question?")) { await api.delete(`/sell/admin/questions/${q.id}`); load({ category_id: cat }); } };
  const toggle = async (q) => { try { await api.put(`/sell/admin/questions/${q.id}`, { ...q, active: q.active === false }); load({ category_id: cat }); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-3">
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="rounded-sm w-64" data-testid="q-cat-select"><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        {cat && <Button className="rounded-sm gap-2 ml-auto" onClick={() => { setForm({ text: "", qtype: "radio", mandatory: true, order: rows.length, options: [] }); setEditId(null); setOpen(true); }} data-testid="add-q-btn"><Plus size={15} /> Add Question</Button>}
      </div>
      {cat && rows.map((q) => (
        <div key={q.id} className={`border border-border rounded-sm bg-card p-3 flex items-start justify-between ${q.active === false ? "opacity-60" : ""}`} data-testid={`q-row-${q.id}`}>
          <div><div className="font-semibold text-sm">{q.text} {q.mandatory && <span className="text-red-500">*</span>}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{q.qtype} · {(q.options || []).map((o) => `${o.label}(${o.points}${o.critical ? "!" : ""})`).join(", ")}</div></div>
          <div className="flex items-center gap-2">
            <ActiveToggle active={q.active !== false} onToggle={() => toggle(q)} testid={`q-toggle-${q.id}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm({ text: q.text, qtype: q.qtype, mandatory: q.mandatory, order: q.order, options: q.options || [] }); setEditId(q.id); setOpen(true); }}><PencilSimple size={15} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(q)}><Trash size={15} /></Button>
          </div>
        </div>
      ))}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-md" data-testid="q-form">
          <SheetHeader><SheetTitle>{editId ? "Edit" : "Add"} Question</SheetTitle></SheetHeader>
          <div className="py-5 space-y-3">
            <div><Label className="label-caps">Question</Label><Input className="rounded-sm mt-1.5" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="q-text" /></div>
            <div><Label className="label-caps">Type</Label><Select value={form.qtype} onValueChange={(v) => setForm({ ...form, qtype: v })}><SelectTrigger className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{["radio", "dropdown", "yesno", "multiselect", "text"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-primary" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} /> Mandatory</label>
            <div>
              <div className="flex items-center justify-between"><Label className="label-caps">Options (points + critical)</Label><Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setForm({ ...form, options: [...form.options, { label: "", points: 0, critical: false }] })} data-testid="add-option">+ Option</Button></div>
              <div className="space-y-2 mt-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Label" className="rounded-sm" value={o.label} onChange={(e) => { const n = [...form.options]; n[i] = { ...o, label: e.target.value }; setForm({ ...form, options: n }); }} />
                    <Input placeholder="Pts" type="number" className="rounded-sm w-20" value={o.points} onChange={(e) => { const n = [...form.options]; n[i] = { ...o, points: e.target.value }; setForm({ ...form, options: n }); }} />
                    <label className="flex items-center gap-1 text-xs whitespace-nowrap"><input type="checkbox" checked={o.critical} onChange={(e) => { const n = [...form.options]; n[i] = { ...o, critical: e.target.checked }; setForm({ ...form, options: n }); }} /> scrap</label>
                    <button className="text-destructive" onClick={() => setForm({ ...form, options: form.options.filter((_, x) => x !== i) })}><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter><Button className="rounded-sm" onClick={save} data-testid="q-save">Save</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LabelTab() {
  const { rows, load } = useCrud("/sell/admin/image-labels");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", media_type: "image", required: false, order: 0 });
  const [editId, setEditId] = useState(null);
  useEffect(() => { load(); }, [load]);
  const save = async () => { try { if (editId) await api.put(`/sell/admin/image-labels/${editId}`, form); else await api.post("/sell/admin/image-labels", { ...form, category_id: "" }); toast.success("Saved"); setOpen(false); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (l) => { if (window.confirm("Delete?")) { await api.delete(`/sell/admin/image-labels/${l.id}`); load(); } };
  const patch = async (l, changes) => { try { await api.put(`/sell/admin/image-labels/${l.id}`, { ...l, ...changes }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-3">
        <Button className="rounded-sm gap-2" onClick={() => { setForm({ label: "", media_type: "image", required: false, visible: true, order: rows.length }); setEditId(null); setOpen(true); }} data-testid="add-label-btn"><Plus size={15} /> Add Label</Button>
        <p className="text-xs text-muted-foreground">Toggle <b>Show to customer</b> to control which photos/videos appear in the sell wizard. <b>Required</b> forces upload before estimate.</p>
      </div>
      <div className="border border-border rounded-sm bg-card">
        <Table><TableHeader><TableRow className="bg-secondary/50"><TableHead>Label</TableHead><TableHead>Type</TableHead><TableHead>Show to customer</TableHead><TableHead>Required</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
          <TableBody data-testid="label-table">{rows.map((l) => (
            <TableRow key={l.id} data-testid={`label-row-${l.id}`}><TableCell className="font-medium">{l.label}</TableCell><TableCell className="capitalize">{l.media_type}</TableCell>
              <TableCell><ActiveToggle active={l.visible !== false} onToggle={() => patch(l, { visible: l.visible === false })} testid={`label-visible-${l.id}`} /></TableCell>
              <TableCell><button onClick={() => patch(l, { required: !l.required })} data-testid={`label-required-${l.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${l.required ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-secondary text-muted-foreground border-border"}`}>{l.required ? "Required" : "Optional"}</button></TableCell>
              <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm(l); setEditId(l.id); setOpen(true); }}><PencilSimple size={15} /></Button><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(l)}><Trash size={15} /></Button></TableCell></TableRow>
          ))}</TableBody></Table>
      </div>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent data-testid="label-form"><SheetHeader><SheetTitle>{editId ? "Edit" : "Add"} Label</SheetTitle></SheetHeader>
        <div className="py-5 space-y-3">
          <div><Label className="label-caps">Label</Label><Input className="rounded-sm mt-1.5" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="label-name" /></div>
          <div><Label className="label-caps">Media Type</Label><Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}><SelectTrigger className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent></Select></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-primary" checked={form.visible !== false} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="label-visible-check" /> Show to customer</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-primary" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} /> Required</label>
          <div><Label className="label-caps">Order</Label><Input type="number" className="rounded-sm mt-1.5" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
        </div>
        <SheetFooter><Button className="rounded-sm" onClick={save} data-testid="label-save">Save</Button></SheetFooter></SheetContent></Sheet>
    </div>
  );
}

function SlotTab() {
  const { rows, load } = useCrud("/sell/admin/slots");
  const [label, setLabel] = useState("");
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!label) return; await api.post("/sell/admin/slots", { label, order: rows.length, active: true }); setLabel(""); load(); };
  const del = async (s) => { await api.delete(`/sell/admin/slots/${s.id}`); load(); };
  return (
    <div className="mt-4 space-y-3 max-w-lg">
      <div className="flex gap-2"><Input placeholder="e.g. 9:00 AM - 11:00 AM" className="rounded-sm" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="slot-input" /><Button className="rounded-sm" onClick={add} data-testid="add-slot-btn">Add</Button></div>
      <div className="space-y-2" data-testid="slot-list">{rows.map((s) => (
        <div key={s.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2"><span className="text-sm">{s.label}</span><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(s)}><Trash size={15} /></Button></div>
      ))}</div>
    </div>
  );
}

function CatalogTab({ cats, reloadCats }) {
  const [cat, setCat] = useState("");
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [bname, setBname] = useState(""); const [mname, setMname] = useState(""); const [selBrand, setSelBrand] = useState("");
  const loadBrands = useCallback(() => { if (cat) api.get(`/sell/admin/brands?category_id=${cat}`).then((r) => setBrands(r.data)); }, [cat]);
  useEffect(() => { loadBrands(); }, [loadBrands]);
  const loadModels = useCallback(() => { if (selBrand) api.get(`/sell/admin/models?brand_id=${selBrand}`).then((r) => setModels(r.data)); }, [selBrand]);
  useEffect(() => { loadModels(); }, [loadModels]);
  const addBrand = async () => { if (!bname) return; await api.post("/sell/admin/brands", { category_id: cat, name: bname, brand_base_price: 0 }); setBname(""); loadBrands(); };
  const addModel = async () => { if (!mname || !selBrand) return; await api.post("/sell/admin/models", { category_id: cat, brand_id: selBrand, name: mname }); setMname(""); loadModels(); };
  const toggleCat = async (c) => { try { await api.put(`/sell/admin/categories/${c.id}`, { ...c, active: c.active === false }); reloadCats && reloadCats(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleBrand = async (b) => { try { await api.put(`/sell/admin/brands/${b.id}`, { ...b, active: b.active === false }); loadBrands(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleModel = async (m) => { try { await api.put(`/sell/admin/models/${m.id}`, { ...m, active: m.active === false }); loadModels(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={cat} onValueChange={(v) => { setCat(v); setSelBrand(""); setModels([]); }}><SelectTrigger className="rounded-sm w-64" data-testid="catalog-cat-select"><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.active === false ? " (disabled)" : ""}</SelectItem>)}</SelectContent></Select>
        <ExcelBar label="Catalog" exportPath="/sell/admin/catalog/export" exportName="sell_catalog.xlsx" importPath="/sell/admin/catalog/import" onDone={() => { reloadCats && reloadCats(); loadBrands(); loadModels(); }} />
      </div>

      <div className="border border-border rounded-sm bg-card p-3">
        <p className="font-heading font-semibold mb-2">Categories</p>
        <div className="flex flex-wrap gap-2" data-testid="catalog-categories">
          {cats.map((c) => (
            <div key={c.id} className={`flex items-center gap-2 border rounded-sm px-2.5 py-1.5 ${c.active === false ? "opacity-70 border-border" : "border-border"}`}>
              <span className="text-sm font-medium">{c.name}</span>
              <ActiveToggle active={c.active !== false} onToggle={() => toggleCat(c)} testid={`cat-toggle-${c.id}`} />
            </div>
          ))}
        </div>
      </div>

      {cat && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="font-heading font-semibold mb-2">Brands</p>
            <div className="flex gap-2 mb-3"><Input placeholder="Brand name" className="rounded-sm" value={bname} onChange={(e) => setBname(e.target.value)} data-testid="brand-input" /><Button className="rounded-sm" onClick={addBrand} data-testid="add-brand-btn">Add</Button></div>
            <div className="space-y-1.5">{brands.map((b) => (
              <div key={b.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border text-sm ${selBrand === b.id ? "border-primary bg-primary/5" : "border-border"} ${b.active === false ? "opacity-70" : ""}`} data-testid={`brand-row-${b.id}`}>
                <button onClick={() => setSelBrand(b.id)} className="text-left flex-1">{b.name}</button>
                <ActiveToggle active={b.active !== false} onToggle={() => toggleBrand(b)} testid={`brand-toggle-${b.id}`} />
              </div>
            ))}</div>
          </div>
          <div>
            <p className="font-heading font-semibold mb-2">Models {selBrand && `(${brands.find((b) => b.id === selBrand)?.name})`}</p>
            {selBrand ? (<>
              <div className="flex gap-2 mb-3"><Input placeholder="Model name" className="rounded-sm" value={mname} onChange={(e) => setMname(e.target.value)} data-testid="model-input" /><Button className="rounded-sm" onClick={addModel} data-testid="add-model-btn">Add</Button></div>
              <div className="space-y-1.5">{models.map((m) => (
                <div key={m.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-border text-sm ${m.active === false ? "opacity-70" : ""}`} data-testid={`model-row-${m.id}`}>
                  <span>{m.name}</span>
                  <ActiveToggle active={m.active !== false} onToggle={() => toggleModel(m)} testid={`model-toggle-${m.id}`} />
                </div>
              ))}</div>
            </>) : <p className="text-sm text-muted-foreground">Select a brand to manage models.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
